import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import gameSettingsService from '../services/gameSettingsService';

// Raccourcis par défaut
const DEFAULT_KEYBINDINGS: Record<string, string> = {
  menu: 'Escape',
  map: 'm',
  inventory: 'i',
  journal: 'j',
  settings: 'o',
  chat: 'Enter',
};

interface UseKeyboardShortcutsOptions {
  onMenuToggle?: () => void;
  onMapToggle?: () => void;
  onInventoryToggle?: () => void;
  onJournalToggle?: () => void;
  onSettingsToggle?: () => void;
  onChatToggle?: () => void;
  enabled?: boolean;
}

/**
 * Hook pour gérer les raccourcis clavier personnalisés
 */
export const useKeyboardShortcuts = (options: UseKeyboardShortcutsOptions = {}) => {
  const navigate = useNavigate();
  const {
    onMenuToggle,
    onMapToggle,
    onInventoryToggle,
    onJournalToggle,
    onSettingsToggle,
    onChatToggle,
    enabled = true,
  } = options;

  // Fonction pour parser une combinaison de touches
  const parseKeyCombo = useCallback((combo: string): {
    key: string;
    ctrl: boolean;
    alt: boolean;
    shift: boolean;
    meta: boolean;
  } => {
    const parts = combo.split('+').map(p => p.trim());
    return {
      key: parts[parts.length - 1], // La dernière partie est toujours la touche principale
      ctrl: parts.includes('Control'),
      alt: parts.includes('Alt'),
      shift: parts.includes('Shift'),
      meta: parts.includes('Meta'),
    };
  }, []);

  // Fonction pour vérifier si un événement correspond à une combinaison
  const matchesKeyCombo = useCallback((
    event: KeyboardEvent,
    combo: string
  ): boolean => {
    const parsed = parseKeyCombo(combo);
    
    // Normaliser la touche de l'événement
    let eventKey = event.key;
    
    // Gérer les cas spéciaux
    if (eventKey === ' ') eventKey = 'Space';
    
    // Normaliser la touche de la combinaison
    let comboKey = parsed.key;
    if (comboKey === ' ') comboKey = 'Space';
    
    // Comparaison insensible à la casse pour les lettres simples
    const eventKeyLower = eventKey.toLowerCase();
    const comboKeyLower = comboKey.toLowerCase();
    
    // Vérifier si les touches correspondent (insensible à la casse pour les lettres)
    const keysMatch = eventKeyLower === comboKeyLower || eventKey === comboKey;
    
    return (
      keysMatch &&
      event.ctrlKey === parsed.ctrl &&
      event.altKey === parsed.alt &&
      event.shiftKey === parsed.shift &&
      event.metaKey === parsed.meta
    );
  }, [parseKeyCombo]);

  useEffect(() => {
    if (!enabled) return;

    let keyBindings: Record<string, string> = DEFAULT_KEYBINDINGS;
    let listenerAttached = false;

    // Charger les paramètres
    const loadSettings = async () => {
      try {
        const result = await gameSettingsService.getSettings();
        if (result.success && result.data) {
          const data = result.data;
          keyBindings = { ...DEFAULT_KEYBINDINGS, ...(data.keyBindings || {}) };
        }
      } catch (error) {
        console.error('Erreur lors du chargement des raccourcis:', error);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignorer si on est dans un input, textarea, ou contentEditable
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Permettre Escape même dans les inputs pour fermer les modales
        if (event.key === 'Escape' && (keyBindings.menu === 'Escape' || keyBindings.menu.toLowerCase() === 'escape')) {
          if (onMenuToggle) {
            event.preventDefault();
            onMenuToggle();
          }
        }
        return;
      }

      // Vérifier chaque raccourci
      if (matchesKeyCombo(event, keyBindings.menu)) {
        event.preventDefault();
        if (onMenuToggle) {
          onMenuToggle();
        }
      } else if (matchesKeyCombo(event, keyBindings.map)) {
        event.preventDefault();
        if (onMapToggle) {
          onMapToggle();
        } else {
          // Action par défaut : naviguer vers la carte
          navigate('/game/map');
        }
      } else if (matchesKeyCombo(event, keyBindings.inventory)) {
        event.preventDefault();
        if (onInventoryToggle) {
          onInventoryToggle();
        }
      } else if (matchesKeyCombo(event, keyBindings.journal)) {
        event.preventDefault();
        if (onJournalToggle) {
          onJournalToggle();
        }
      } else if (matchesKeyCombo(event, keyBindings.settings)) {
        event.preventDefault();
        if (onSettingsToggle) {
          onSettingsToggle();
        } else {
          // Action par défaut : ouvrir les paramètres (nécessite d'être sur le dashboard)
          navigate('/dashboard');
        }
      } else if (matchesKeyCombo(event, keyBindings.chat)) {
        event.preventDefault();
        if (onChatToggle) {
          onChatToggle();
        }
      }
    };

    // Charger les paramètres et attacher le listener
    loadSettings().then(() => {
      if (!listenerAttached) {
        window.addEventListener('keydown', handleKeyDown);
        listenerAttached = true;
      }
    });

    return () => {
      if (listenerAttached) {
        window.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [
    enabled,
    navigate,
    onMenuToggle,
    onMapToggle,
    onInventoryToggle,
    onJournalToggle,
    onSettingsToggle,
    onChatToggle,
    matchesKeyCombo,
  ]);
};

